import { useRef, useEffect } from "react";
import * as d3 from "d3";


// Gauge dial component
const DEFAULT_SEGMENTS = [
  { start: 0, end: 2, color: "#117733" },
  { start: 2, end: 3.5, color: "#d6cf04" },
  { start: 3.5, end: 5, color: "#E69F00" },
  { start: 5, end: 10, color: "#d62728" },
];

export default function GaugeDial({ value, min = 0, max = 10, width = 320, height = 260, arcWidth = 34, transitionDuration = 1000, segments = DEFAULT_SEGMENTS }) {

    const ref = useRef();
    useEffect(() => {
      const svg = d3.select(ref.current);
      svg.selectAll("*").remove();
      const centerX = width / 2;
      // Place the center at the bottom of the SVG, but leave more space above
      const centerY = height - 40;
      const radius = Math.min(width, height * 2) / 2 - 10;
      // Use a semicircle: π (left) to 0 (right), center at bottom
      const scale = d3.scaleLinear().domain([min, max]).range([ - Math.PI / 2, Math.PI / 2]);
      // Draw colored arcs
      segments.forEach(seg => {
        svg.append("path")
          .attr("d", d3.arc()
            .innerRadius(radius - arcWidth)
            .outerRadius(radius)
            .startAngle(scale(seg.start))
            .endAngle(scale(seg.end))
          )
          .attr("fill", seg.color)
          .attr("transform", `translate(${centerX},${centerY})`);
      });
      // Draw blue arc for value
      const valueAngle = scale(Math.max(min, Math.min(value, max)));
      svg.append("path")
        .attr("d", d3.arc()
          .innerRadius(radius - arcWidth + 7)
          .outerRadius(radius - 5)
          .startAngle(scale(min))
          .endAngle(scale(min))
        )
        .attr("fill", "#3182ce")
        .attr("transform", `translate(${centerX},${centerY})`)
        .transition()
        .duration(transitionDuration)
        .ease(d3.easeQuadOut)
        .attrTween("d", function() {
          const startAngle = scale(min);
          const endAngle = valueAngle;
          const interpolate = d3.interpolate(startAngle, endAngle);
          return function(t) {
            const angle = interpolate(t);
            return d3.arc()
              .innerRadius(radius - arcWidth + 7)
              .outerRadius(radius - 5)
              .startAngle(startAngle)
              .endAngle(angle)();
          };
        });
      // Draw ticks and labels — derived from segment boundaries
      const ticks = [...new Set(segments.flatMap(s => [s.start, s.end]))];
      const tickScale = d3.scaleLinear().domain([min, max]).range([Math.PI, 2 * Math.PI]);
      ticks.forEach(tick => {
  
        // d3 Arc starts at the top and goes clockwise
        // so we need to add π/2 to get the correct angle
        const angle = tickScale(tick);
        const x1 = centerX + Math.cos(angle) * (radius - arcWidth - 4);
        const y1 = centerY + Math.sin(angle) * (radius - arcWidth - 4);
        const x2 = centerX + Math.cos(angle) * (radius + 6);
        const y2 = centerY + Math.sin(angle) * (radius + 6);
        svg.append("line")
          .attr("x1", x1).attr("y1", y1).attr("x2", x2).attr("y2", y2)
          .attr("stroke", "#444").attr("stroke-width", 1.2);
        // Tick label
        const lx = centerX + Math.cos(angle) * (radius + 18);
        const ly = centerY + Math.sin(angle) * (radius + 18);
        svg.append("text")
          .attr("x", lx)
          .attr("y", ly)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("font-size", 13)
          .attr("fill", "#222")
          .text(tick.toFixed(1).replace(/\.0$/, ""));
      });
      // Draw value text (center)
      svg.append("text")
        .attr("x", centerX)
        .attr("y", centerY - radius / 4)
        .attr("text-anchor", "middle")
        .attr("font-size", 44)
        .attr("fill", "#333")
        .text(value.toFixed(2));


        
      // Draw qualitative labels (now outside the arc)
      // Left label (No action needed)
      {
        const x = centerX + Math.cos(Math.PI) * (radius + 32) + 8; // nudge right for visual centering
        const y = centerY - (Math.sin(Math.PI) * (radius + 32) + 34); // raise up
        const label = svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", "middle")
          .attr("font-size", 15)
          .attr("fill", "#222");
        label.append("tspan").attr("x", x - 18).attr("dy", 0).text("No action");
        label.append("tspan").attr("x", x - 18).attr("dy", "1.2em").text("needed");
      }


      // Top label (Query Data)
      svg.append("text")
        .attr("x", centerX + Math.cos(2 * Math.PI / 3) * (radius + 32))
        .attr("y", centerY - (Math.sin(2 * Math.PI / 3) * (radius + 32) + 6))
        .attr("text-anchor", "middle")
        .attr("font-size", 15)
        .attr("fill", "#222")
        .text("Query Data");


      // Right label (Intervention needed)
      {
        const x = centerX + Math.cos(Math.PI / 6) * (radius + 32) - 10; // nudge left for visual centering
        const y = centerY - (Math.sin(Math.PI / 6) * (radius + 32) + 34); // raise up
        const label = svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", "middle")
          .attr("font-size", 15)
          .attr("fill", "#222");
        label.append("tspan").attr("x", x + 10).attr("dy", 0).text("Intervention");
        label.append("tspan").attr("x", x + 10).attr("dy", "1.2em").text("needed");
      }
    }, [value, min, max, width, height, arcWidth, transitionDuration, segments]);
    return <div style={{ overflow: 'visible', width }}><svg ref={ref} width={width} height={height} overflow="visible" /></div>;
  }